import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import api from "@/lib/api";
import TeamLogo from "@/components/sportsbook/TeamLogo";
import { leagueLabel } from "@/components/sportsbook/SBBoardGame";
import { OddsButton } from "@/components/sportsbook/sb";
import { useBetSlip } from "@/context/BetSlipContext";
import { sortSportsbookEvents } from "@/lib/sportsbookPriority";

/* SportsSearch — global sportsbook search (design, README §2.6). Desktop ⌘/Ctrl-K
   modal (680px, 96px from top), mobile full screen. Result groups in order
   Players · Teams · Events · Markets, built client-side from the cached /events
   board plus any /events/:id/markets loaded when the modal opens (v1). Opened by the
   `pnr-open-sb-search` event (TopBar + board button) or Ctrl/⌘K under /sportsbook or
   /event. Exposes a stable search(index, query)→groups shape so a server endpoint can
   replace the index later. */

const initialsOf = (name = "") => {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "—";
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[w.length - 1][0]).toUpperCase();
};
const isGame = (e) => !["outright", "prop"].includes(e.event_type) && e.home_team && e.away_team;

// Build the base (teams + events) index from the cached board, and the extra
// (players + markets) index from any loaded /events/:id/markets.
export function buildIndex(events, marketsByEvent) {
  const board = (events || []).filter(isGame);
  const teamsMap = new Map();
  const eventRows = [];
  board.forEach((e) => {
    [e.away_team, e.home_team].forEach((t) => {
      if (!t) return;
      const key = t.toLowerCase();
      if (!teamsMap.has(key)) teamsMap.set(key, { id: key, name: t, title: t, sub: leagueLabel(e.league) || e.sport, league: e.league, sport: e.sport, eventId: e.id });
    });
    eventRows.push({
      id: e.id, title: `${e.away_team} @ ${e.home_team}`, sub: leagueLabel(e.league) || e.sport,
      away: e.away_team, home: e.home_team, league: e.league, sport: e.sport, live: e.status === "live",
    });
  });

  const playerMap = new Map();
  const marketRows = [];
  Object.values(marketsByEvent || {}).forEach((pack) => {
    const ev = pack?.event; if (!ev) return;
    const evLabel = `${ev.away_team} @ ${ev.home_team}`;
    const live = ev.status === "live";
    (pack.players || []).forEach((p) => {
      const pkey = `${p.player}|${ev.id}`;
      if (!playerMap.has(pkey)) playerMap.set(pkey, { id: pkey, name: p.player, title: p.player, sub: `Player props · ${evLabel}`, eventId: ev.id, live });
      const byProp = {};
      (p.outcomes || []).forEach((o) => {
        const k = `${p.prop_short || o.prop_short || ""}|${o.line}`;
        const m = (byProp[k] = byProp[k] || { player: p.player, prop: o.prop_short || "", line: o.line, over: null, under: null });
        if (o.side === "under") m.under = o; else m.over = o;
      });
      Object.values(byProp).forEach((m) => {
        if (!m.over && !m.under) return;
        marketRows.push({
          id: `${p.player}|${m.prop}|${m.line}|${ev.id}`,
          title: `${p.player} ${m.prop}`.trim(), sub: evLabel,
          eventId: ev.id, eventLabel: evLabel, league: ev.league, sport: ev.sport, live,
          line: m.line, over: m.over, under: m.under,
        });
      });
    });
  });

  return { teamRows: [...teamsMap.values()], eventRows, playerRows: [...playerMap.values()], marketRows };
}

export function search(index, query) {
  const q = (query || "").trim().toLowerCase();
  const cap = (a) => a.slice(0, 4);
  const match = (s) => String(s || "").toLowerCase().includes(q);
  if (!q) {
    return { players: cap(index.playerRows), teams: cap(index.teamRows), events: cap(index.eventRows), markets: cap(index.marketRows) };
  }
  return {
    players: cap(index.playerRows.filter((p) => match(p.name))),
    teams: cap(index.teamRows.filter((t) => match(t.name))),
    events: cap(index.eventRows.filter((e) => match(e.title) || match(e.sub) || match(e.away) || match(e.home))),
    markets: cap(index.marketRows.filter((m) => match(m.title) || match(m.sub))),
  };
}

function Avatar({ name }) {
  return (
    <span className="flex shrink-0 items-center justify-center rounded-full font-display font-extrabold text-[#D4D4D8]"
      style={{ width: 36, height: 36, fontSize: 12, background: "radial-gradient(circle at 50% 30%, #26262B, #18181C)", boxShadow: "inset 0 0 0 1px #2F2F35" }}>
      {initialsOf(name)}
    </span>
  );
}

function GroupRow({ row, type, active, onClick, onPickOdds, isPicked }) {
  const common = { height: 56, outline: active ? "2px solid #00D632" : "none", outlineOffset: -2, background: active ? "#141417" : "transparent" };
  if (type === "market") {
    return (
      <div data-testid={`sb-search-result-market-${row.id}`} className="flex w-full items-center gap-3 rounded-xl px-3" style={common}>
        <span className="flex shrink-0 items-center justify-center rounded-lg" style={{ width: 36, height: 36, background: "#18181C", boxShadow: "inset 0 0 0 1px #26262B" }}>
          <Icon name="layers" size={16} className="text-[#00D632]" />
        </span>
        <button type="button" onClick={onClick} className="min-w-0 flex-1 text-left">
          <span className="block truncate" style={{ font: "700 15px Outfit", color: "#fff" }}>{row.title}</span>
          <span className="block truncate" style={{ font: "500 12px Outfit", color: "#8A8A93" }}>{row.sub}</span>
        </button>
        <div className="flex shrink-0 gap-1.5">
          <div className="w-[62px] sm:w-[92px]">{row.over ? <OddsButton variant="stack" line={`O ${row.line ?? ""}`.trim()} price={row.over.odds} selected={isPicked(row.eventId, row.over.id)} onClick={() => onPickOdds(row, row.over, "over")} testid={`sb-search-odds-${row.id}-o`} /> : <OddsButton na variant="stack" />}</div>
          <div className="w-[62px] sm:w-[92px]">{row.under ? <OddsButton variant="stack" line={`U ${row.line ?? ""}`.trim()} price={row.under.odds} selected={isPicked(row.eventId, row.under.id)} onClick={() => onPickOdds(row, row.under, "under")} testid={`sb-search-odds-${row.id}-u`} /> : <OddsButton na variant="stack" />}</div>
        </div>
      </div>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={`sb-search-result-${type}-${row.id || row.name}`}
      className="flex w-full items-center gap-3 rounded-xl px-3 text-left" style={common}>
      {type === "event" ? (
        <span className="relative flex shrink-0" style={{ width: 36 }}>
          <TeamLogo name={row.away} league={row.league} sport={row.sport} size={24} />
          <span className="absolute left-3 top-1"><TeamLogo name={row.home} league={row.league} sport={row.sport} size={24} /></span>
        </span>
      ) : type === "player" ? (
        <Avatar name={row.name} />
      ) : (
        <TeamLogo name={row.name} league={row.league} sport={row.sport} size={36} />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate" style={{ font: "700 15px Outfit", color: "#fff" }}>{row.title}</span>
        <span className="block truncate" style={{ font: "500 12px Outfit", color: "#8A8A93" }}>{row.sub}</span>
      </span>
      {row.live && <span className="rounded px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-[#EF4444]" style={{ background: "rgba(239,68,68,.12)" }}>Live</span>}
      <Icon name="chevron-right" size={13} className="shrink-0 text-[#52525b]" />
    </button>
  );
}

function Group({ title, rows, type, baseIndex, activeIndex, onPick, onPickOdds, isPicked }) {
  if (!rows.length) return null;
  return (
    <div className="mb-1">
      <div className="flex items-center gap-2 px-3 pb-1 pt-2">
        <span style={{ font: "800 10px Outfit", letterSpacing: ".18em", color: "#71717a", textTransform: "uppercase" }}>{title}</span>
        <span style={{ font: "700 10px Manrope", color: "#52525b" }}>{rows.length}</span>
      </div>
      {rows.map((row, i) => (
        <GroupRow key={row.id || row.name} row={row} type={type} active={activeIndex === baseIndex + i}
          onClick={() => onPick(row, type)} onPickOdds={onPickOdds} isPicked={isPicked} />
      ))}
    </div>
  );
}

export default function SportsSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [events, setEvents] = useState([]);
  const [marketsByEvent, setMarketsByEvent] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { has, toggle } = useBetSlip();

  const close = useCallback(() => { setOpen(false); setQuery(""); setActiveIndex(0); }, []);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("pnr-open-sb-search", openHandler);
    const keyHandler = (e) => {
      const onSB = /^\/(sportsbook|event)/.test(window.location.pathname);
      if ((e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === "k" && onSB) { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener("keydown", keyHandler);
    return () => { window.removeEventListener("pnr-open-sb-search", openHandler); window.removeEventListener("keydown", keyHandler); };
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get("/events").then(({ data }) => {
      const evs = Array.isArray(data) ? data : [];
      setEvents(evs);
      // Load markets for the most relevant games (live first) so Players/Markets
      // populate — client-side v1 index. Cap the fan-out; cache across opens.
      // Load markets for live games AND games from prop-likely sports (where player
      // props live) so the Players/Markets groups can populate — client-side v1 index.
      const games = sortSportsbookEvents(evs.filter(isGame)); // premium games indexed first (P0-A)
      const live = games.filter((e) => e.status === "live").slice(0, 6);
      const propLikely = games.filter((e) => ["football", "basketball", "baseball", "hockey"].includes((e.sport || "").toLowerCase())).slice(0, 10);
      const picks = [...new Map([...live, ...propLikely].map((e) => [e.id, e])).values()].slice(0, 14);
      setMarketsByEvent((prev) => {
        const need = picks.filter((e) => !prev[e.id]);
        need.forEach((e) => {
          api.get(`/events/${e.id}/markets`).then(({ data: d }) => {
            if (!d?.event) return;
            setMarketsByEvent((cur) => ({ ...cur, [e.id]: { event: d.event, players: d.props_by_player || [] } }));
          }).catch(() => {});
        });
        return prev;
      });
    }).catch(() => {});
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  const index = useMemo(() => buildIndex(events, marketsByEvent), [events, marketsByEvent]);
  const groups = useMemo(() => search(index, query), [index, query]);
  const flat = useMemo(() => [
    ...groups.players.map((r) => ({ r, type: "player" })),
    ...groups.teams.map((r) => ({ r, type: "team" })),
    ...groups.events.map((r) => ({ r, type: "event" })),
    ...groups.markets.map((r) => ({ r, type: "market" })),
  ], [groups]);
  const empty = query.trim() && flat.length === 0;

  const pick = useCallback((row, type) => {
    const id = type === "event" ? row.id : row.eventId;
    if (id) navigate(`/event/${id}`);
    close();
  }, [navigate, close]);

  const isPicked = useCallback((eventId, outcomeId) => has(eventId, String(outcomeId)), [has]);
  const pickOdds = useCallback((row, o) => {
    toggle({ eventId: row.eventId, outcomeId: String(o.id), label: `${row.title} ${o.side === "under" ? "Under" : "Over"} ${row.line ?? ""}`.trim(),
      odds: o.odds, marketVersion: o.market_version ?? null, line: o.line ?? null, side: o.side ?? null,
      receivedAt: o.received_at ?? null, providerUpdatedAt: o.provider_updated_at ?? null, expiresAt: o.expires_at ?? null,
      eventLabel: row.eventLabel, market: o.market || "prop", league: row.league || null, sport: row.sport || null, live: !!row.live,
      propKey: `${o.player || ""}|${o.prop_type || row.prop || ""}|${o.line ?? row.line ?? ""}` });
  }, [toggle]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") { close(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, flat.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
    if (e.key === "Enter" && flat[activeIndex]) { e.preventDefault(); const { r, type } = flat[activeIndex]; if (type !== "market") pick(r, type); }
  };

  if (!open) return null;
  const baseTeams = groups.players.length;
  const baseEvents = baseTeams + groups.teams.length;
  const baseMarkets = baseEvents + groups.events.length;

  return (
    <div className="fixed inset-0 z-[120] flex justify-center" style={{ background: "rgba(5,5,6,.66)" }} onMouseDown={close} data-testid="sb-search">
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="flex h-full w-full flex-col overflow-hidden sm:mt-24 sm:h-auto sm:max-h-[640px] sm:max-w-[680px] sm:rounded-[20px]"
        style={{ background: "#0E0E11", boxShadow: "inset 0 0 0 1px #26262B, inset 0 1px 0 rgba(255,255,255,.06), 0 30px 80px rgba(0,0,0,.6)" }}
      >
        {/* input row — desktop: 64px + Esc keycap; mobile: 44px green-ring field + Cancel */}
        <div className="hidden items-center gap-3 px-4 sm:flex" style={{ height: 64, borderBottom: "1px solid #1F1F23" }}>
          <Icon name="search" size={18} className="shrink-0 text-[#00D632]" />
          <input ref={inputRef} value={query} onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }} onKeyDown={onKeyDown}
            data-testid="sb-search-input" placeholder="Teams, players, games, markets"
            className="h-full flex-1 bg-transparent text-white placeholder:text-[#52525b] focus:outline-none" style={{ font: "700 18px Outfit" }} />
          <button type="button" onClick={close} className="shrink-0" style={{ background: "#18181C", boxShadow: "inset 0 0 0 1px #2F2F35", borderRadius: 7, padding: "3px 8px", font: "700 11px Manrope", color: "#a1a1aa" }}>Esc</button>
        </div>
        <div className="flex items-center gap-2 px-3 sm:hidden" style={{ height: 56 }}>
          <div className="flex h-11 flex-1 items-center gap-2.5 rounded-xl px-3" style={{ background: "#0E0E11", boxShadow: "inset 0 0 0 1.5px #00D632" }}>
            <Icon name="search" size={18} className="shrink-0 text-[#00D632]" />
            <input value={query} onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }} onKeyDown={onKeyDown}
              data-testid="sb-search-input-m" placeholder="Teams, players, games"
              className="h-full flex-1 bg-transparent text-white placeholder:text-[#52525b] focus:outline-none" style={{ font: "600 16px Outfit" }} />
          </div>
          <button type="button" onClick={close} className="shrink-0 px-1 text-[15px] font-semibold text-[#00D632]">Cancel</button>
        </div>
        {/* results */}
        <div className="min-h-0 flex-1 overflow-y-auto py-1">
          {empty ? (
            <div className="px-6 py-12 text-center">
              <p className="font-display text-[17px] font-extrabold text-white">No matches for “{query}”</p>
              <p className="mx-auto mt-1.5 max-w-[360px] text-[13px] text-[#71717a]">Search covers games and markets currently on the board. Check the spelling or try a team or league.</p>
            </div>
          ) : (
            <>
              <Group title="Players" rows={groups.players} type="player" baseIndex={0} activeIndex={activeIndex} onPick={pick} isPicked={isPicked} onPickOdds={pickOdds} />
              <Group title="Teams" rows={groups.teams} type="team" baseIndex={baseTeams} activeIndex={activeIndex} onPick={pick} isPicked={isPicked} onPickOdds={pickOdds} />
              <Group title="Events" rows={groups.events} type="event" baseIndex={baseEvents} activeIndex={activeIndex} onPick={pick} isPicked={isPicked} onPickOdds={pickOdds} />
              <Group title="Markets" rows={groups.markets} type="market" baseIndex={baseMarkets} activeIndex={activeIndex} onPick={pick} onPickOdds={pickOdds} isPicked={isPicked} />
            </>
          )}
        </div>
        {/* footer — desktop only (SBX-M3 has no footer) */}
        <div className="hidden items-center justify-between px-4 sm:flex" style={{ height: 44, borderTop: "1px solid #1F1F23" }}>
          <span className="text-[11px] text-[#71717a]">Results from games and markets on the board right now</span>
          <span className="text-[11px] text-[#71717a]">↑ ↓ to move · Enter to open</span>
        </div>
      </div>
    </div>
  );
}
